import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { ListWithHeading } from '../../partials/lists/listWithHeading/ListWithHeading';
import { OnTopCard } from '../../partials/cards/onTopCard/OnTopCard';
import { useTheme } from '../../../theming/useTheme';
import { CardComponentDataProps } from '../../partials/cards/dividedCard/DividedCard';
import { VrLineHeight } from '../../../theming/verticalRhythm/verticalRhythm';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';

const componentConfigurationKey = 'headlinesArticleList';

const createStyles = (vrLineHeight: VrLineHeight) => StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.25}px`,
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`,
    },
  },
  cardWrapper: {
    boxSizing: 'border-box',
    paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
      flex: '0 0 66.67%',
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flex: '0 0 66.67%',
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flex: '0 0 66.67%',
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingRight: '0',
      flex: '0 0 100%',
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 4}px`,
    },
  },
  listWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
      flex: '0 1 33.33%',
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flex: '0 1 33.33%',
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flex: '0 1 33.33%',
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingLeft: '0',
      flex: '0 1 100%',
      paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 4}px`,
    },
  },
});

interface HeadlinesArticleListComponentDataProps {
  cardsProps: CardComponentDataProps[]
  displayName: string
}

interface HeadlinesArticleListComponentThemeProps {
  vrLineHeight: VrLineHeight
}

type HeadlinesArticleListComponentProps = HeadlinesArticleListComponentDataProps & HeadlinesArticleListComponentThemeProps;

export const HeadlinesArticleListComponent: React.FunctionComponent<HeadlinesArticleListComponentProps> = ({ cardsProps, displayName, vrLineHeight }) => {
  const styles = createStyles(vrLineHeight);
  const [onTopCardProps, ...listProps] = cardsProps;
  return (
    <div className={css(styles.container)}>
      <div className={css(styles.cardWrapper)}>
        <OnTopCard {...onTopCardProps} />
      </div>
      <div className={css(styles.listWrapper)}>
        <ListWithHeading listProps={listProps} displayName={displayName} itemNumLines={2} />
      </div>
    </div>
  );
};

const mapThemeToProps = (theme: HeadlinesArticleListComponentThemeProps) => ({
  vrLineHeight: theme.vrLineHeight,
});

export const HeadlinesArticleList = (props: HeadlinesArticleListComponentDataProps) => {
  const themeContext = useTheme(componentConfigurationKey, mapThemeToProps);
  return <ErrorBoundary><HeadlinesArticleListComponent {...themeContext} {...props} /></ErrorBoundary>;
};
